import { Box, Grid } from '@mui/material';
import { ComponentType, PropsWithChildren } from 'react';

import { ContractVariant } from '~/common/types';
import { ModuleConfigProvider } from '~/components/ModuleConfigProvider';
import { SourcingCaseContactWidget } from '~/modules/contractDemands/SourcingCaseContactWidget/SourcingCaseContactWidget';
import { NegotiationSettingsWidget } from '~/modules/demand/NegotiationSettingsWidget/NegotiationSettingsWidget.tsx';
import { NegotiationTermsWidget } from '~/modules/NegotiationTermsWidget/NegotiationTermsWidget.tsx';
import { CountryMapWidget } from '~/routes/experiments/RiskSustainability/widgets/CountryMapWidget/CountryMapWidget';
import { CountryReportListWidget } from '~/routes/experiments/RiskSustainability/widgets/CountryReportListWidget/CountryReportListWidget';
import { CountryReportTableWidget } from '~/routes/experiments/RiskSustainability/widgets/CountryReportTableWidget/CountryReportTableWidget';
import { ManagedSupplierWidget } from '~/routes/experiments/RiskSustainability/widgets/ManagedSupplierWidget/ManagedSupplierWidget';
import { SupplierListWidget } from '~/routes/experiments/RiskSustainability/widgets/SupplierListWidget/SupplierListWidget';
import { SupplierRiskAlertWidget } from '~/routes/experiments/RiskSustainability/widgets/SupplierRiskAlertWidget/SupplierRiskAlertWidget';
import { SupplierRiskCategoryScoreWidget } from '~/routes/experiments/RiskSustainability/widgets/SupplierRiskCategoryScoreWidget/SupplierRiskCategoryScoreWidget';
import { SupplierRiskScoreWidget } from '~/routes/experiments/RiskSustainability/widgets/SupplierRiskScoreWidget/SupplierRiskScoreWidget';
import { SupplierRisksWidget } from '~/routes/experiments/RiskSustainability/widgets/SupplierRisksWidget/SupplierRisksWidget';

import { ActiveContractsQuickViewWidget } from './ActiveContractsQuickViewWidget/ActiveContractsQuickViewWidget';
import { ActiveContractsWidget } from './ActiveContractsWidget/ActiveContractsWidget';
import { AutomaticAcceptanceTimingWidget } from './AutomaticAcceptanceTimingWidget/AutomaticAcceptanceTimingWidget';
import { AverageNegotiationSavingsWidget } from './AverageNegotiationSavingsWidget/AverageNegotiationSavingsWidget';
import { SupplierAverageNegotiationSavingsWidget } from './AverageNegotiationSavingsWidget/SupplierAverageNegotiationSavingsWidget';
import { CommodityGroupSupplierListWidget } from './base/CommodityGroupSupplierListWidget/CommodityGroupSupplierListWidget';
import { NegotiationCountWidget } from './base/NegotiationCountWidget/NegotiationCountWidget';
import { SupplierNegotiationCountWidget } from './base/NegotiationCountWidget/SupplierNegotiationCountWidget';
import { NegotiationProcessWidget } from './base/NegotiationProcessWidget/NegotiationProcessWidget';
import { CommodityGroupPurchaseOrderCountWidget } from './base/PurchaseOrderCountWidget/CommodityGroupPurchaseOrderCountWidget';
import { PurchaseOrderCountWidget } from './base/PurchaseOrderCountWidget/PurchaseOrderCountWidget';
import { SupplierPurchaseOrderCountWidget } from './base/PurchaseOrderCountWidget/SupplierPurchaseOrderCountWidget';
import { CommodityGroupPurchaseOrderItemListWidget } from './base/PurchaseOrderItemListWidget/CommodityGroupPurchaseOrderItemListWidget';
import { SupplierPurchaseOrderItemWidget } from './base/PurchaseOrderItemListWidget/SupplierPurchaseOrderItemWidget';
import { CommodityGroupPurchaseOrderVolumeShareWidget } from './base/PurchaseOrderVolumeShareWidget/CommodityGroupPurchaseOrderVolumeWidget';
import { SupplierPurchaseOrderVolumeShareWidget } from './base/PurchaseOrderVolumeShareWidget/SupplierPurchaseOrderVolumeWidget';
import { CommodityGroupPurchaseOrderVolumeWidget } from './base/PurchaseOrderVolumeWidget/CommodityGroupPurchaseOrderVolumeWidget';
import { PurchaseOrderVolumeWidget } from './base/PurchaseOrderVolumeWidget/PurchaseOrderVolumeWidget';
import { SupplierPurchaseOrderVolumeWidget } from './base/PurchaseOrderVolumeWidget/SupplierPurchaseOrderVolumeWidget';
import { SourcingProjectInformationWidget } from './base/SourcingProjectInformationWidget/SourcingProjectInformationWidget';
import { SourcingProjectRequestListWidget } from './base/SourcingProjectRequestListWidget/SourcingProjectRequestListWidget';
import { SourcingProjectSupplierWidget } from './base/SourcingProjectSupplierWidget/SourcingProjectSupplierWidget';
import { SupplierContactListWidget } from './base/SupplierContactListWidget/SupplierContactListWidget';
import { SupplierDetailsWidget } from './base/SupplierDetailsWidget/SupplierDetailsWidget';
import { BusinessLogWidget } from './BusinessLogWidget/BusinessLogWidget';
import { CommoditySupplierListWidget } from './CommoditySupplierListWidget/CommoditySupplierListWidget';
import { CompanyDetailsWidget } from './CompanyDetailsWidget/CompanyDetailsWidget';
import { ContractAdherenceWidget } from './ContractAdherenceWidget/ContractAdherenceWidget';
import { ContractAlertsWidget } from './ContractAlertsWidget/ContractAlertsWidget';
import { ContractDemandAwardingWidget } from './contractDemands/ContractDemandAwardingWidget/ContractDemandAwardingWidget';
import { ContractDemandCaseListWidget } from './contractDemands/ContractDemandCaseListWidget/ContractDemandCaseListWidget';
import { ContractDemandDetailWidget } from './contractDemands/ContractDemandDetailWidget/ContractDemandDetailWidget';
import { ContractDemandFileAttachmentsWidget } from './contractDemands/ContractDemandFileAttachmentsWidget/ContractDemandFileAttachmentsWidget';
import { ContractDemandRequestWidget } from './contractDemands/ContractDemandRequestWidget/ContractDemandRequestWidget';
import { CoverageAssistantWidget } from './contractDemands/CoverageAssistantWidget/CoverageAssistantWidget';
import { SourcingCaseInternalFileAttachmentsWidget } from './contractDemands/SourcingCaseBuyerFileAttachmentsWidget/SourcingCaseInternalFileAttachmentsWidget';
import { SourcingCaseFlowWidget } from './contractDemands/SourcingCaseFlowWidget/SourcingCaseFlowWidget';
import { SourcingCaseNegotiationSettingsWidget } from './contractDemands/SourcingCaseNegotiationSettingsWidget/SourcingCaseNegotiationSettingsWidget';
import { SourcingCaseQuoteWidget } from './contractDemands/SourcingCaseQuoteWidget/SourcingCaseQuoteWidget';
import { SourcingCaseSupplierFileAttachmentsWidget } from './contractDemands/SourcingCaseSupplierFileAttachmentsWidget/SourcingCaseSupplierFileAttachmentsWidget';
import { ContractDetailsWidget } from './ContractDetailsWidget/ContractDetailsWidget';
import { ContractsTotalWidget } from './ContractsTotalWidget/ContractsTotalWidget';
import { ContractUtilizationQuickViewWidget } from './ContractUtilizationQuickViewWidget/ContractUtilizationQuickViewWidget';
import { ContractValidityWidget } from './ContractValidityWidget/ContractValidityWidget';
import { DeliveryReliabilityQuickViewWidget } from './DeliveryReliabilityQuickViewWidget/DeliveryReliabilityQuickViewWidget';
import { DeliveryReliabilityWidget } from './DeliveryReliabilityWidget/DeliveryReliabilityWidget';
import { DemandDocumentWidget } from './demand/DemandDocumentWidget/DemandDocumentWidget';
import { AwardingConfigurationWidget } from './DemandAwardingWidget/components/AwardingConfigurationWidget/AwardingConfigurationWidget';
import { DemandAwardingWidget } from './DemandAwardingWidget/DemandAwardingWidget';
import { DemandAwardingLineItemBiddingWidget } from './DemandAwardingWidget/lineItemBidding/DemandAwardingLineItemBiddingWidget';
import { DemandCasesWidget } from './DemandCasesWidget/DemandCasesWidget';
import { DemandDetailWidget } from './DemandDetailWidget/DemandDetailWidget';
import { DemandFileAttachmentsWidget } from './DemandFileAttachmentsWidget/DemandFileAttachmentsWidget';
import { DemandRecommendationsCountWidget } from './DemandRecommendationsCountWidget/DemandRecommendationsCountWidget';
import { DemandRecommendationWidget } from './DemandRecommendationWidget/DemandRecommendationWidget';
import { DemandRequestWidget } from './DemandRequestWidget/DemandRequestWidget';
import { EmissionCommodityWidget } from './EmissionCommodityWidget/EmissionCommodityWidget';
import { GeneralEmissionCommodityWidget } from './GeneralEmissionCommodityWidget/GeneralEmissionCommodityWidget';
import { GeneralEmissionWidget } from './GeneralEmissionWidget/GeneralEmissionWidget';
import { GridDivider } from './GridDivider/GridDivider';
import { LargeViewMaterialWidget } from './LargeViewMaterialWidget/LargeViewMaterialWidget';
import { MaterialOrderedQuantityWidget } from './MaterialOrderedQuantityWidget/MaterialOrderedQuantityWidget';
import { MaterialOrderedVolumeWidget } from './MaterialOrderedVolumeWidget/MaterialOrderedVolumeWidget';
import { BaseModule, ModuleType } from './Module.types';
import { ModuleWithProp } from './Module.utils';
import { NegotiatedQuoteWidget } from './NegotiatedQuoteWidget/NegotiatedQuoteWidget';
import { NegotiationCaseDetailIssuesWidget } from './NegotiationCaseDetailIssuesWidget/NegotiationCaseDetailIssuesWidget';
import { NegotiationCaseDetailStatusWidget } from './NegotiationCaseDetailStatusWidget/NegotiationCaseDetailStatusWidget';
import { NegotiationCaseInternalFileAttachmentsWidget } from './NegotiationCaseInternalFileAttachmentsWidget/NegotiationCaseInternalFileAttachmentsWidget';
import { NegotiationCaseSupplierFileAttachmentsWidget } from './NegotiationCaseSupplierFileAttachmentsWidget/NegotiationCaseSupplierFileAttachmentsWidget';
import { NegotiationContactWidget } from './NegotiationContactWidget/NegotiationContactWidget';
import { NegotiationQuoteDetailsWidget } from './NegotiationQuoteDetailsWidget/NegotiationQuoteDetailsWidget';
import { NegotiationReminderSettingsWidget } from './NegotiationReminderSettingsWidget/NegotiationReminderSettingsWidget';
import { NegotiationSpendWidget } from './NegotiationSpendWidget/NegotiationSpendWidget';
import { SupplierNegotiationSpendWidget } from './NegotiationSpendWidget/SupplierNegotiationSpendWidget';
import { NegotiationsSavingsCommodityGroupWidget } from './NegotiationsSavingsCommodityGroupWidget/NegotiationsSavingsCommodityGroupWidget';
import { NegotiationsTotalSavingsWidget } from './NegotiationsTotalSavingsWidget/NegotiationsTotalSavingsWidget';
import { SupplierNegotiationsTotalSavingsWidget } from './NegotiationsTotalSavingsWidget/SupplierNegotiationsTotalSavingsWidget';
import { NegotiationSupplierContactWidget } from './NegotiationSupplierContactWidget/NegotiationSupplierContactWidget';
import { NegotiationSupplierIntroductionWidget } from './NegotiationSupplierIntroductionWidget/NegotiationSupplierIntroductionWidget';
import { NegotiationTasksWidget } from './NegotiationTasksWidget/NegotiationTasksWidget';
import { NewsroomWidget } from './NewsroomWidget/NewsroomWidget';
import { OpenPurchaseRequisitionsWidget } from './OpenPurchaseRequisitionsWidget/OpenPurchaseRequisitionsWidget';
import { GeneralOrderVolumeCommodityGroupWidget } from './OrderVolumeCommodityGroupWidget/GeneralOrderVolumeCommodityGroupWidget';
import { SupplierOrderVolumeCommodityGroupWidget } from './OrderVolumeCommodityGroupWidget/SupplierOrderVolumeCommodityGroupWidget';
import { PercentageOfDemandsWithRecommendationsWidget } from './PercentageOfDemandsWithRecommendationsWidget/PercentageOfDemandsWithRecommendationsWidget';
import { PurchaseOrderDetailsWidget } from './PurchaseOrderDetailsWidget/PurchaseOrderDetailsWidget';
import { PurchaseOrderEmissionWidget } from './PurchaseOrderEmissionWidget/PurchaseOrderEmissionWidget';
import { PurchaseRequisitionDetailsWidget } from './PurchaseRequisitionDetailsWidget/PurchaseRequisitionDetailsWidget';
import { SampleCubeWidget } from './SampleCubeWidget/SampleCubeWidget';
import { SourcingCaseIssueWidget } from './SourcingCaseIssueWidget/SourcingCaseIssueWidget';
import { SpendAnalysisWidget } from './SpendAnalysisWidget/SpendAnalysisWidget';
import { SupplierEmissionWidget } from './SupplierEmissionWidget/SupplierEmissionWidget';
import { SupplierLocationWidget } from './SupplierLocationWidget/SupplierLocationWidget';
import { UserAvailabilityWidget } from './UserAvailabilityWidget/UserAvailabilityWidget';
import { UserProfileAbsenceWidget } from './UserProfileAbsenceWidget/UserProfileAbsenceWidget';
import { UserProfileDetailsWidget } from './UserProfileDetailsWidget/UserProfileDetailsWidget';

const typeMap: { [key in ModuleType]: ComponentType<any> | undefined } = {
  [ModuleType.ACTIVE_CONTRACTS_QUICKVIEW_WIDGET]: ActiveContractsQuickViewWidget,
  [ModuleType.ACTIVE_CONTRACTS_WIDGET]: ActiveContractsWidget,
  [ModuleType.CONTRACT_ALERTS_WIDGET]: ContractAlertsWidget,
  [ModuleType.CONTRACTS_TOTAL_QUANTITY_WIDGET]: ModuleWithProp(ContractsTotalWidget, {
    variant: ContractVariant.QUANTITY,
  }),
  [ModuleType.CONTRACTS_TOTAL_VOLUME_WIDGET]: ModuleWithProp(ContractsTotalWidget, { variant: ContractVariant.VALUE }),
  [ModuleType.DELIVERY_RELIABILITY_QUICKVIEW_WIDGET]: DeliveryReliabilityQuickViewWidget,
  [ModuleType.DELIVERY_RELIABILITY_WIDGET]: DeliveryReliabilityWidget,
  [ModuleType.EMISSION_COMMODITY_WIDGET]: EmissionCommodityWidget,
  [ModuleType.NEWSROOM_WIDGET]: NewsroomWidget,
  [ModuleType.SPEND_ANALYSIS_WIDGET]: SpendAnalysisWidget,
  [ModuleType.DIVIDER]: GridDivider,
  [ModuleType.MATERIAL_ORDERED_QUANTITY_WIDGET]: MaterialOrderedQuantityWidget,
  [ModuleType.MATERIAL_ORDERED_VOLUME_WIDGET]: MaterialOrderedVolumeWidget,
  [ModuleType.PURCHASE_ORDER_EMISSION_WIDGET]: PurchaseOrderEmissionWidget,
  [ModuleType.SUPPLIER_PURCHASE_ORDER_COUNT_WIDGET]: SupplierPurchaseOrderCountWidget,
  [ModuleType.CONTRACT_VALIDITY_WIDGET]: ContractValidityWidget,
  [ModuleType.CONTRACT_ADHERENCE_WIDGET]: ContractAdherenceWidget,
  [ModuleType.CONTRACT_UTILIZATION_WIDGET]: ContractUtilizationQuickViewWidget,
  [ModuleType.OPEN_PURCHASE_REQUISITIONS_WIDGET]: OpenPurchaseRequisitionsWidget,
  [ModuleType.CUBE_SAMPLE]: SampleCubeWidget,
  [ModuleType.COMMODITY_SUPPLIER_LIST_WIDGET]: CommoditySupplierListWidget,
  [ModuleType.LARGE_VIEW_MATERIAL_WIDGET]: LargeViewMaterialWidget,
  // Base Order
  [ModuleType.PURCHASE_ORDER_DETAILS_WIDGET]: PurchaseOrderDetailsWidget,
  // Base Contract
  [ModuleType.CONTRACT_DETAILS_WIDGET]: ContractDetailsWidget,
  [ModuleType.PURCHASE_REQUISITION_DETAILS_WIDGET]: PurchaseRequisitionDetailsWidget,
  // Base General
  [ModuleType.GENERAL_EMISSION_WIDGET]: GeneralEmissionWidget,
  [ModuleType.GENERAL_EMISSION_COMMODITY_WIDGET]: GeneralEmissionCommodityWidget,
  [ModuleType.PURCHASE_ORDER_COUNT_WIDGET]: PurchaseOrderCountWidget,
  [ModuleType.PURCHASE_ORDER_VOLUME_WIDGET]: PurchaseOrderVolumeWidget,
  [ModuleType.GENERAL_ORDER_VOLUME_COMMODITY_GROUP_WIDGET]: GeneralOrderVolumeCommodityGroupWidget,
  [ModuleType.GENERAL_DEMAND_RECOMMENDATIONS_COUNT_WIDGET]: DemandRecommendationsCountWidget,
  [ModuleType.GENERAL_PERCENTAGE_OF_DEMANDS_WITH_RECOMMENDATIONS_WIDGET]: PercentageOfDemandsWithRecommendationsWidget,
  // Base Supplier
  [ModuleType.SUPPLIER_PURCHASE_ORDER_ITEM_LIST_WIDGET]: SupplierPurchaseOrderItemWidget,
  [ModuleType.SUPPLIER_PURCHASE_ORDER_VOLUME_WIDGET]: SupplierPurchaseOrderVolumeWidget,
  [ModuleType.SUPPLIER_PURCHASE_ORDER_SHARE_WIDGET]: SupplierPurchaseOrderVolumeShareWidget,
  [ModuleType.SUPPLIER_ORDER_VOLUME_COMMODITY_GROUP_WIDGET]: SupplierOrderVolumeCommodityGroupWidget,
  [ModuleType.SUPPLIER_EMISSION_WIDGET]: SupplierEmissionWidget,
  [ModuleType.SUPPLIER_DETAILS_WIDGET]: SupplierDetailsWidget,
  [ModuleType.COMPANY_DETAILS_WIDGET]: CompanyDetailsWidget,
  [ModuleType.SUPPLIER_LOCATION_WIDGET]: SupplierLocationWidget,
  [ModuleType.SUPPLIER_CONTACT_LIST_WIDGET]: SupplierContactListWidget,

  // Base Commodity Group
  [ModuleType.COMMODITY_GROUP_SUPPLIER_LIST_WIDGET]: CommodityGroupSupplierListWidget,
  [ModuleType.COMMODITY_GROUP_PURCHASE_ORDER_ITEM_LIST_WIDGET]: CommodityGroupPurchaseOrderItemListWidget,
  [ModuleType.COMMODITY_GROUP_PURCHASE_ORDER_VOLUME_WIDGET]: CommodityGroupPurchaseOrderVolumeWidget,
  [ModuleType.COMMODITY_GROUP_PURCHASE_ORDER_COUNT_WIDGET]: CommodityGroupPurchaseOrderCountWidget,
  [ModuleType.COMMODITY_GROUP_PURCHASE_ORDER_SHARE_WIDGET]: CommodityGroupPurchaseOrderVolumeShareWidget,
  //
  [ModuleType.BUSINESS_LOG_WIDGET]: BusinessLogWidget,
  // Demand
  [ModuleType.DEMAND_DETAIL_WIDGET]: DemandDetailWidget,
  [ModuleType.DEMAND_AWARDING_CONFIGURATION_WIDGET]: AwardingConfigurationWidget,
  [ModuleType.DEMAND_REQUEST_WIDGET]: DemandRequestWidget,
  [ModuleType.DEMAND_AWARDING_WIDGET]: DemandAwardingWidget,
  [ModuleType.DEMAND_AWARDING_LINE_ITEM_BIDDING_WIDGET]: DemandAwardingLineItemBiddingWidget,
  [ModuleType.DEMAND_CASES_WIDGET]: DemandCasesWidget,
  [ModuleType.DEMAND_RECOMMENDATION_WIDGET]: DemandRecommendationWidget,
  [ModuleType.DEMAND_ATTACHMENT_WIDGET]: DemandDocumentWidget,
  [ModuleType.DEMAND_FILE_ATTACHMENTS_WIDGET]: DemandFileAttachmentsWidget,
  // Negotiation Case
  [ModuleType.NEGOTIATION_TERMS]: NegotiationTermsWidget,
  [ModuleType.NEGOTIATION_SETTINGS]: NegotiationSettingsWidget,
  [ModuleType.NEGOTIATION_QUOTE_DETAILS]: NegotiationQuoteDetailsWidget,
  [ModuleType.NEGOTIATION_TASKS]: NegotiationTasksWidget,
  [ModuleType.NEGOTIATION_NEGOTIATED_QUOTE]: NegotiatedQuoteWidget,
  [ModuleType.NEGOTIATION_SUPPLIER_CONTACT]: NegotiationSupplierContactWidget,
  [ModuleType.NEGOTIATION_CONTACT]: NegotiationContactWidget,
  [ModuleType.NEGOTIATION_SUPPLIER_INTRODUCTION_MESSAGE]: NegotiationSupplierIntroductionWidget,
  [ModuleType.NEGOTIATIONS_PROCESS_METRICS_WIDGET]: NegotiationProcessWidget,
  [ModuleType.NEGOTIATION_CASE_DETAIL_STATUS]: NegotiationCaseDetailStatusWidget,
  [ModuleType.NEGOTIATION_CASE_DETAIL_ISSUES]: NegotiationCaseDetailIssuesWidget,
  [ModuleType.NEGOTIATION_BUYER_FILE_ATTACHMENTS]: NegotiationCaseInternalFileAttachmentsWidget,
  [ModuleType.NEGOTIATION_SUPPLIER_FILE_ATTACHMENTS]: NegotiationCaseSupplierFileAttachmentsWidget,
  // RunAI Analytics
  // General
  [ModuleType.NEGOTIATIONS_COUNT_WIDGET]: NegotiationCountWidget,
  [ModuleType.NEGOTIATIONS_AVERAGE_SAVINGS_WIDGET]: AverageNegotiationSavingsWidget,
  [ModuleType.NEGOTIATIONS_TOTAL_SAVINGS_WIDGET]: NegotiationsTotalSavingsWidget,
  [ModuleType.NEGOTIATIONS_SPEND]: NegotiationSpendWidget,
  [ModuleType.NEGOTIATIONS_SAVINGS_COMMODITY_GROUP_WIDGET]: NegotiationsSavingsCommodityGroupWidget,
  // Supplier
  [ModuleType.SUPPLIER_NEGOTIATIONS_COUNT_WIDGET]: SupplierNegotiationCountWidget,
  [ModuleType.SUPPLIER_NEGOTIATIONS_AVERAGE_SAVINGS_WIDGET]: SupplierAverageNegotiationSavingsWidget,
  [ModuleType.SUPPLIER_NEGOTIATIONS_TOTAL_SAVINGS_WIDGET]: SupplierNegotiationsTotalSavingsWidget,
  [ModuleType.SUPPLIER_NEGOTIATIONS_SPEND]: SupplierNegotiationSpendWidget,
  // Risk
  [ModuleType.SUPPLIER_RISK_SCORE_WIDGET]: SupplierRiskScoreWidget,
  [ModuleType.SUPPLIER_RISK_CATEGORIES_WIDGET]: SupplierRiskCategoryScoreWidget,
  [ModuleType.SUPPLIER_RISKS]: SupplierRisksWidget,
  [ModuleType.RISK_MANAGED_SUPPLIER_WIDGET]: ManagedSupplierWidget,
  [ModuleType.SUPPLIER_RISK_ALERT_WIDGET]: SupplierRiskAlertWidget,
  [ModuleType.RISK_COUNTRY_REPORT_LIST_WIDGET]: CountryReportListWidget,
  [ModuleType.RISK_SUPPLIER_LIST_WIDGET]: SupplierListWidget,
  [ModuleType.RISK_COUNTRY_REPORT_TABLE_WIDGET]: CountryReportTableWidget,
  [ModuleType.RISK_COUNTRY_MAP]: CountryMapWidget,

  // User Profile
  [ModuleType.USER_PROFILE_DETAILS_WIDGET]: UserProfileDetailsWidget,
  [ModuleType.USER_PROFILE_ABSENCE_WIDGET]: UserProfileAbsenceWidget,
  [ModuleType.USER_AVAILABILITY]: UserAvailabilityWidget,

  // Sourcing Project
  [ModuleType.SOURCING_PROJECT_INFORMATIONS_WIDGET]: SourcingProjectInformationWidget,
  [ModuleType.SOURCING_PROJECT_SUPPLIER_WIDGET]: SourcingProjectSupplierWidget,
  [ModuleType.SOURCING_PROJECT_REQUEST_LIST_WIDGET]: SourcingProjectRequestListWidget,

  // Contract Demands & Sourcing cases

  [ModuleType.CONTRACT_DEMAND_DETAIL_WIDGET]: ContractDemandDetailWidget,
  [ModuleType.CONTRACT_DEMAND_REQUEST_WIDGET]: ContractDemandRequestWidget,
  [ModuleType.CONTRACT_DEMAND_CASE_LIST_WIDGET]: ContractDemandCaseListWidget,
  [ModuleType.CONTRACT_DEMAND_AWARDING_WIDGET]: ContractDemandAwardingWidget,
  [ModuleType.CONTRACT_DEMAND_FILE_ATTACHMENTS_WIDGET]: ContractDemandFileAttachmentsWidget,

  [ModuleType.SOURCING_CASE_QUOTE_WIDGET]: SourcingCaseQuoteWidget,
  [ModuleType.SOURCING_CASE_CONTACT_WIDGET]: SourcingCaseContactWidget,
  [ModuleType.SOURCING_CASE_NEGOTIATION_SETTINGS]: SourcingCaseNegotiationSettingsWidget,
  [ModuleType.SOURCING_CASE_FLOW_WIDGET]: SourcingCaseFlowWidget,
  [ModuleType.SOURCING_CASE_ISSUE_WIDGET]: SourcingCaseIssueWidget,
  [ModuleType.SOURCING_CASE_BUYER_FILE_ATTACHMENTS_WIDGET]: SourcingCaseInternalFileAttachmentsWidget,
  [ModuleType.SOURCING_CASE_SUPPLIER_FILE_ATTACHMENTS_WIDGET]: SourcingCaseSupplierFileAttachmentsWidget,

  // reminder settings
  [ModuleType.NEGOTIATION_REMINDER_SETTINGS]: NegotiationReminderSettingsWidget,
  [ModuleType.AUTOMATIC_ACCEPTANCE_TIMING]: AutomaticAcceptanceTimingWidget,

  // COVERAGE ASSISTANT

  [ModuleType.COVERAGE_ASSISTANT_WIDGET]: CoverageAssistantWidget,
};

export interface ModuleProps extends BaseModule {
  disabled?: boolean;
}

export const Module = ({
  disabled,
  children,
  moduleType,
  grid,
  props,
  config,
  ...otherProps
}: PropsWithChildren<ModuleProps>) => {
  const ModuleComponent = typeMap[moduleType as ModuleType];
  if (ModuleComponent) {
    let result = (
      <ModuleConfigProvider value={config}>
        <ModuleComponent {...props} {...otherProps} />
      </ModuleConfigProvider>
    );

    if (grid) {
      // wrap with relative container to display delete button correctly
      if (children) {
        result = (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flex: '1',
              position: 'relative',
            }}
          >
            {result}
            {children}
          </Box>
        );
      }
      result = (
        <Grid container {...grid}>
          {result}
        </Grid>
      );
    }

    return result;
  }
  return null;
};
